import React, { useState, useEffect, useCallback, useContext } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_FOC_ITEMS_REPORT, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import * as XLSX from 'xlsx';

const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const buildInitialFilters = () => {
    const today = formatLocalDate(new Date());
    return {
        fromDate: today,
        toDate: today,
        sourceDocument: 'Pharmacy',
    };
};

const formatDateString = (value) => {
    if (!value) return '-';
    try {
        return new Date(value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    } catch {
        return value;
    }
};

const formatExpiryDate = (value) => {
    if (!value) return '-';
    try {
        const date = new Date(value);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${month}/${year}`;
    } catch {
        return value;
    }
};

const formatInvoiceNoWithSequence = (row) => {
    const inv = row.InvoiceNo;
    const seq = row.Sequence;
    if (inv == null && seq == null) return '-';
    if (seq == null || seq === '') return inv != null ? String(inv) : '-';
    return `${inv}/${seq}`;
};

const summaryColumns = [
    { key: 'ItemNo', label: 'Item No' },
    { key: 'ItemName', label: 'Item Name' },
    { key: 'TotalFOCQty', label: 'Total FOC Qty', align: 'right' },
    { key: 'TotalPurchaseQty', label: 'Total Purchase Qty', align: 'right' },
    { key: 'LineCount', label: 'Lines', align: 'right' },
    { key: 'LastReceivedDate', label: 'Last Received' },
];

const detailColumns = [
    { key: 'InvoiceDate', label: 'Invoice Date' },
    { key: 'SupplierName', label: 'Supplier' },
    { key: 'PurchaseRefNo', label: 'Ref No' },
    { key: 'InvoiceNo', label: 'Inv / Seq' },
    { key: 'BatchCode', label: 'Batch' },
    { key: 'ExpiryDate', label: 'Expiry' },
    { key: 'Quantity', label: 'Qty', align: 'right' },
    { key: 'FOC', label: 'FOC', align: 'right' },
    { key: 'Price', label: 'Price', align: 'right' },
    { key: 'Total', label: 'Total', align: 'right' },
    { key: 'MRP', label: 'MRP', align: 'right' },
    { key: 'TaxAmount', label: 'Tax', align: 'right' },
];

const getNetQty = (detail) =>
    Number(detail.Quantity || 0) - Number(detail.FOC || 0);

const renderDetailCell = (column, detail) => {
    switch (column.key) {
        case 'InvoiceDate':
            return formatDateString(detail.InvoiceDate);
        case 'ExpiryDate':
            return formatExpiryDate(detail.ExpiryDate);
        case 'InvoiceNo':
            return formatInvoiceNoWithSequence(detail);
        case 'Quantity':
            return getNetQty(detail);
        case 'Price':
        case 'Total':
        case 'MRP':
        case 'TaxAmount':
            return parseFloat(detail[column.key] || 0).toFixed(2);
        default:
            return detail[column.key] ?? '-';
    }
};

export const FOCItemsReport = () => {
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(buildInitialFilters);
    const [reportData, setReportData] = useState([]);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const token = localStorage.getItem('accessToken');

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({ ...prev, [filterId]: value }));
    };

    const toggleItemExpansion = (itemNo) => {
        setExpandedItems((prev) => {
            const next = new Set(prev);
            if (next.has(itemNo)) {
                next.delete(itemNo);
            } else {
                next.add(itemNo);
            }
            return next;
        });
    };

    const getFOCItemsReport = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}${GET_FOC_ITEMS_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    sourceDocument: filters.sourceDocument || 'Pharmacy',
                },
            });

            if (response.status === 200 && response.data?.isSucess) {
                const data = response.data.data || [];
                const sortedData = [...data].sort((a, b) =>
                    (a.ItemName || '').localeCompare(b.ItemName || '')
                );
                setReportData(sortedData);
                setExpandedItems(new Set());
                if (sortedData.length === 0) {
                    toast.info('No FOC items found for the selected period', { autoClose: 2000 });
                } else {
                    toast.success('Report generated successfully', { autoClose: 1000 });
                }
            } else {
                toast.error(response.data?.message || 'Failed to fetch FOC items report', { autoClose: 2000 });
                setReportData([]);
            }
        } catch (error) {
            console.error('Failed to fetch FOC items report:', error);
            toast.error('Failed to fetch FOC items report', { autoClose: 2000 });
            setReportData([]);
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    const handleExport = () => {
        if (reportData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }

        try {
            const summaryRows = reportData.map((row, index) => ({
                'Sl.': index + 1,
                'Item No': row.ItemNo,
                'Item Name': row.ItemName || '',
                'Total FOC Qty': Number(row.TotalFOCQty || 0),
                'Total Purchase Qty': Number(row.TotalPurchaseQty || 0),
                'Line Count': Number(row.LineCount || 0),
                'Last Received Date': formatDateString(row.LastReceivedDate),
            }));

            const detailRows = [];
            reportData.forEach((row) => {
                (row.details || []).forEach((detail) => {
                    detailRows.push({
                        'Item No': row.ItemNo,
                        'Item Name': row.ItemName || '',
                        'Invoice Date': formatDateString(detail.InvoiceDate),
                        'Supplier': detail.SupplierName || '',
                        'Source': detail.SourceDocument || '',
                        'Ref No': detail.PurchaseRefNo || '',
                        'Inv / Seq': formatInvoiceNoWithSequence(detail),
                        'Batch': detail.BatchCode || '',
                        'Expiry': formatExpiryDate(detail.ExpiryDate),
                        'Qty': getNetQty(detail),
                        'FOC': Number(detail.FOC || 0),
                        'Price': Number(parseFloat(detail.Price || 0).toFixed(3)),
                        'Total': Number(parseFloat(detail.Total || 0).toFixed(2)),
                        'MRP': Number(parseFloat(detail.MRP || 0).toFixed(2)),
                        'Discount': Number(parseFloat(detail.DiscountAmount || 0).toFixed(2)),
                        'Tax': Number(parseFloat(detail.TaxAmount || 0).toFixed(2)),
                    });
                });
            });

            const wb = XLSX.utils.book_new();
            const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
            summarySheet['!cols'] = [
                { wch: 5 }, { wch: 10 }, { wch: 35 }, { wch: 14 },
                { wch: 18 }, { wch: 12 }, { wch: 16 },
            ];
            XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

            const detailSheet = XLSX.utils.json_to_sheet(detailRows);
            detailSheet['!cols'] = [
                { wch: 10 }, { wch: 30 }, { wch: 14 }, { wch: 22 },
                { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
                { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
                { wch: 10 }, { wch: 10 }, { wch: 10 },
            ];
            XLSX.utils.book_append_sheet(wb, detailSheet, 'Details');

            const fromDateStr = filters.fromDate || 'All';
            const toDateStr = filters.toDate || 'All';
            XLSX.writeFile(wb, `FOC_Items_Report_${fromDateStr}_to_${toDateStr}.xlsx`);
            toast.success(`Exported ${reportData.length} items`, { autoClose: 2000 });
        } catch (error) {
            console.error('Error exporting FOC items report:', error);
            toast.error('Failed to export report', { autoClose: 2000 });
        }
    };

    const totalPages = Math.ceil(reportData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = reportData.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters.fromDate, filters.toDate, filters.sourceDocument, reportData.length]);

    const totals = {
        items: reportData.length,
        focQty: reportData.reduce((sum, row) => sum + Number(row.TotalFOCQty || 0), 0),
        purchaseQty: reportData.reduce((sum, row) => sum + Number(row.TotalPurchaseQty || 0), 0),
        lines: reportData.reduce((sum, row) => sum + Number(row.LineCount || 0), 0),
    };

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Purchase Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        FOC Items Report
                                    </h1>
                                </div>
                            )}
                            <form className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                    From Date
                                    <input
                                        type="date"
                                        value={filters.fromDate ?? ''}
                                        onChange={(event) => handleFilterChange('fromDate', event.target.value)}
                                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                    />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                    To Date
                                    <input
                                        type="date"
                                        value={filters.toDate ?? ''}
                                        onChange={(event) => handleFilterChange('toDate', event.target.value)}
                                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                    />
                                </label>
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                    Source Document
                                    <select
                                        value={filters.sourceDocument ?? 'Pharmacy'}
                                        onChange={(event) => handleFilterChange('sourceDocument', event.target.value)}
                                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                    >
                                        <option value="Pharmacy">Pharmacy</option>
                                    </select>
                                </label>
                            </form>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={getFOCItemsReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters());
                                    setReportData([]);
                                    setExpandedItems(new Set());
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={reportData.length === 0}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-4 flex-1 min-h-0 px-1">
                        {/* {reportData.length > 0 && (
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 shrink-0">
                                <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Items with FOC</p>
                                    <p className="mt-1 text-xl font-semibold text-emerald-700">{totals.items}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Total FOC Qty</p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">{totals.focQty}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Total Purchase Qty</p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">{totals.purchaseQty}</p>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice Lines</p>
                                    <p className="mt-1 text-xl font-semibold text-slate-700">{totals.lines}</p>
                                </div>
                            </div>
                        )} */}

                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-1">
                                <p className="text-sm text-slate-500">
                                    {reportData.length > 0 ? (
                                        <>
                                            Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, reportData.length)}</span> of{' '}
                                            <span className="font-semibold text-slate-700">{reportData.length}</span> items.
                                            Expand a row to view invoice-level FOC details.
                                        </>
                                    ) : (
                                        'Select a date range and click Generate to load FOC items.'
                                    )}
                                </p>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full w-full divide-y divide-slate-200 text-sm text-slate-700 border-collapse table-fixed">
                                    <colgroup>
                                        <col className="w-10" />
                                        <col className="w-[8%]" />
                                        <col className="w-[32%]" />
                                        <col className="w-[12%]" />
                                        <col className="w-[14%]" />
                                        <col className="w-[8%]" />
                                        <col className="w-[14%]" />
                                    </colgroup>
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap"></th>
                                            {summaryColumns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${
                                                        column.align === 'right' ? 'text-right' : 'text-left'
                                                    }`}
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {paginatedData.length > 0 ? (
                                            paginatedData.map((row) => {
                                                const itemKey = row.ItemNo;
                                                const isExpanded = expandedItems.has(itemKey);
                                                const details = row.details || [];

                                                return (
                                                    <React.Fragment key={itemKey}>
                                                        <tr className="hover:bg-indigo-50/40 transition">
                                                            <td className="px-3 py-3 whitespace-nowrap">
                                                                {details.length > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleItemExpansion(itemKey)}
                                                                        className="inline-flex items-center justify-center p-1 text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
                                                                        title={isExpanded ? 'Hide details' : 'Show details'}
                                                                    >
                                                                        {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-slate-700">{row.ItemNo}</td>
                                                            <td className="px-4 py-3 font-medium text-slate-800">
                                                                {row.ItemName}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-right font-semibold text-emerald-700">
                                                                {row.TotalFOCQty ?? 0}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-right text-slate-700">
                                                                {row.TotalPurchaseQty ?? 0}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-right text-slate-700">
                                                                {row.LineCount ?? 0}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                                                                {formatDateString(row.LastReceivedDate)}
                                                            </td>
                                                        </tr>
                                                        {isExpanded && details.length > 0 && (
                                                            <tr className="bg-slate-50">
                                                                <td colSpan={summaryColumns.length + 1} className="px-4 py-3">
                                                                    <div className="w-full rounded-xl border border-slate-200 bg-white overflow-auto">
                                                                        <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-indigo-500 border-b border-slate-100">
                                                                            Purchase lines ({details.length})
                                                                        </p>
                                                                        <table className="min-w-full w-full table-fixed text-xs text-slate-700">
                                                                            <colgroup>
                                                                                <col className="w-[9%]" />
                                                                                <col className="w-[14%]" />
                                                                                <col className="w-[8%]" />
                                                                                <col className="w-[8%]" />
                                                                                <col className="w-[8%]" />
                                                                                <col className="w-[7%]" />
                                                                                <col className="w-[7%]" />
                                                                                <col className="w-[7%]" />
                                                                                <col className="w-[8%]" />
                                                                                <col className="w-[8%]" />
                                                                                <col className="w-[7%]" />
                                                                                <col className="w-[9%]" />
                                                                            </colgroup>
                                                                            <thead className="bg-slate-100">
                                                                                <tr>
                                                                                    {detailColumns.map((column) => (
                                                                                        <th
                                                                                            key={column.key}
                                                                                            className={`px-3 py-2 font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${
                                                                                                column.align === 'right' ? 'text-right' : 'text-left'
                                                                                            }`}
                                                                                        >
                                                                                            {column.label}
                                                                                        </th>
                                                                                    ))}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-slate-100">
                                                                                {details.map((detail, detailIndex) => (
                                                                                    <tr key={`${itemKey}-${detailIndex}`} className="hover:bg-indigo-50/30">
                                                                                        {detailColumns.map((column) => (
                                                                                            <td
                                                                                                key={column.key}
                                                                                                className={`px-3 py-2 whitespace-nowrap ${
                                                                                                    column.align === 'right' ? 'text-right' : 'text-left'
                                                                                                } ${column.key === 'FOC' ? 'font-semibold text-emerald-700' : ''}`}
                                                                                            >
                                                                                                {renderDetailCell(column, detail)}
                                                                                            </td>
                                                                                        ))}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={summaryColumns.length + 1} className="px-4 py-8 text-center text-slate-500">
                                                    No FOC items found for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(FOCItemsReport);
